import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export default function Module7Page() {
  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      <header className="bg-[var(--coral-dim)] border-b border-[var(--coral)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link to="/elevation/workbook/oqf" className="text-[var(--coral)] hover:text-[var(--coral)] text-sm mb-4 inline-block">
            ← Back to OQF Home
          </Link>
          <h1 className="text-3xl font-bold text-[var(--text-pri)]">
            Module 7: Make OQF a Habit
          </h1>
          <p className="text-[var(--text-sec)] mt-2">
            Objective: By the end of this module, you will have a practical plan to apply the OQF to your daily work and life.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
            🛠️ Tool: The OQF-Lite (for Daily Use)
          </h2>
          <p className="text-[var(--text-sec)] mb-4">
            You don't always need a 14-column register. For fast-paced meetings or daily check-ins, use this "one-minute method for turning confusion into clarity."
          </p>
          <div className="bg-[var(--bg-raised)] p-4 border border-[var(--coral)]">
            <p className="font-semibold text-[var(--text-pri)] mb-3">⚡ Open Questions Framework — Short-Form (OQF-Lite)</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--surface)]">
                    <th className="px-4 py-2 text-left font-semibold text-[var(--text-pri)]">Step</th>
                    <th className="px-4 py-2 text-left font-semibold text-[var(--text-pri)]">Prompt</th>
                    <th className="px-4 py-2 text-left font-semibold text-[var(--text-pri)]">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--rule)]">
                  <tr>
                    <td className="px-4 py-2 font-medium">1.</td>
                    <td className="px-4 py-2"><strong>What's the real question?</strong></td>
                    <td className="px-4 py-2">Identify the single unknown that's blocking progress.</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">2.</td>
                    <td className="px-4 py-2"><strong>What do we think is true right now?</strong></td>
                    <td className="px-4 py-2">Capture the team's working theory in one sentence.</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">3.</td>
                    <td className="px-4 py-2"><strong>How could we find out fast?</strong></td>
                    <td className="px-4 py-2">Define one smallest decisive action.</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">4.</td>
                    <td className="px-4 py-2"><strong>What did we learn (and what changed)?</strong></td>
                    <td className="px-4 py-2">Record the answer and what it means for action.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
            🚀 Tool: Your "Go-Do-It" Plan
          </h2>
          <p className="text-[var(--text-sec)] mb-4">
            Here's how you can make OQF work for your team starting Monday.
          </p>
          <ol className="list-decimal list-inside space-y-4 text-[var(--text-sec)]">
            <li>
              <strong>Start with a Pilot:</strong> Pick one live project that's "stuck." First Step: Don't start with the register. Start with a conversation. Ask your team, "What are the questions we still haven't answered that could change what we do next?" Write them down.
            </li>
            <li>
              <strong>Introduce the Framework:</strong> Introduce the 4-step <strong>OQF-Lite</strong> version first. It's fast and easy. Frame it as a "clarity system," not an "extra process."
            </li>
            <li>
              <strong>Run the Cadence:</strong> Weekly (30 min): Run a "Weekly OQ Review." Focus only on "Next Steps." Close what's answered, update confidence, and assign new learning actions. Monthly (30 min): Run a "Monthly Clarity Check-In." Look at your metrics (DL, EUR, FR) and celebrate the team's learning.
            </li>
            <li>
              <strong>Embed in Existing Workflows:</strong> Planning: Add an "Open Questions" section to every project brief. Meetings: Use the OQF-Lite prompts to re-center stuck discussions.
            </li>
            <li>
              <strong>Measure and Tell the Story:</strong> After 60 days, review your metrics. Tell the story of how the team's ability to learn faster led to a better decision or outcome.
            </li>
          </ol>
        </div>

        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
            🏁 Your Journey Starts Now
          </h2>
          <p className="text-[var(--text-sec)] mb-2">
            You've completed the workshop, but the real work starts Monday.
          </p>
          <p className="text-[var(--text-sec)] mb-2">
            The goal of the Open Questions Framework isn't to create a perfect, 100-line register. The goal is to build a team <strong>rhythm</strong> of inquiry, discipline, and learning.
          </p>
          <p className="text-[var(--text-sec)]">
            It's about creating a culture where it's safe to say, "I don't know, but here's how I'll find out." It's about transforming uncertainty from a blocker that causes "fog" into an operating system that drives clarity.
          </p>
          <p className="text-[var(--text-pri)] font-semibold mt-4">
            Now, go ask good questions.
          </p>
        </div>

        <div className="flex gap-4">
          <Link to="/elevation/workbook/oqf/module-6" className="px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]">
            ← Previous Module
          </Link>
          <Link to="/elevation/workbook/oqf" className="px-4 py-2 bg-[var(--coral)] text-white hover:bg-[var(--coral)] flex items-center gap-2">
            Back to Home
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}






